import React from 'react';

// One unique medal per rank (1-12), self-hosted at /public/ranks/*.
// Each PNG is 256x143 (landscape 16:9) — the earlier circular-disc container
// was forcing them into 56x56 with object-contain, which letterboxed the
// artwork down to ~56x31 and hid it behind a dark disc. The component now
// renders a rounded-rectangle tile at the artwork's natural aspect ratio so
// every rank badge reads clearly on the Cosmic Rank screen.
const RANK_MEDALS = {
  1:  { name: 'New Vibe',       color: '#B08D57', accent: '#F0C674', src: '/ranks/r1.png' },
  2:  { name: 'Rising Vibe',    color: '#C6743C', accent: '#F5A25C', src: '/ranks/r2.png' },
  3:  { name: 'Active Vibe',    color: '#C0C7D1', accent: '#E8ECF3', src: '/ranks/r3.png' },
  4:  { name: 'Vibe Star',      color: '#F5B301', accent: '#FFD84D', src: '/ranks/r4.png' },
  5:  { name: 'Vibe Leader',    color: '#2FC5B5', accent: '#5EEBDA', src: '/ranks/r5.png' },
  6:  { name: 'Vibe Master',    color: '#8B5CF6', accent: '#C4B5FD', src: '/ranks/r6.png' },
  7:  { name: 'Elite Vibe',     color: '#3AB8FF', accent: '#7CD8FF', src: '/ranks/r7.png' },
  8:  { name: 'Vibe Legend',    color: '#F0B93A', accent: '#FFDA6E', src: '/ranks/r8.png' },
  9:  { name: 'Global Vibe',    color: '#5DA9E9', accent: '#9AC8F5', src: '/ranks/r9.png' },
  10: { name: 'World Champion', color: '#B975FF', accent: '#DDB6FF', src: '/ranks/r10.png' },
  11: { name: 'Ultimate Vibe',  color: '#4FA8FF', accent: '#9AD1FF', src: '/ranks/r11.png' },
  12: { name: 'Zenith Vibe',    color: '#E6E9F5', accent: '#B39CFF', src: '/ranks/r12.png' },
};

// Natural aspect ratio of the medal artwork (256 / 143).
export const MEDAL_ASPECT = 256 / 143;

export function medalForRank(rank) {
  const r = Math.max(1, Math.min(12, Number(rank) || 1));
  return { rank: r, ...RANK_MEDALS[r] };
}

export const ALL_RANK_MEDALS = Array.from({ length: 12 }, (_, i) => ({
  rank: i + 1,
  ...RANK_MEDALS[i + 1],
}));

/**
 * RankMedal — displays rank artwork inside a glowing rounded-rectangle tile.
 * `size` is interpreted as the WIDTH in px; height is derived from the medal's
 * natural aspect ratio so the artwork is never letterboxed or clipped.
 */
export default function RankMedal({ rank = 1, size = 96, animated = true, className = '', ...rest }) {
  const medal = medalForRank(rank);
  const width = size;
  const height = Math.round(size / MEDAL_ASPECT);

  return (
    <div
      {...rest}
      data-testid={rest['data-testid'] || `rank-medal-${medal.rank}`}
      data-rank={medal.rank}
      className={`relative inline-flex items-center justify-center shrink-0 rounded-xl overflow-hidden ${className}`}
      style={{
        width,
        height,
        background: `radial-gradient(circle at 50% 50%, ${medal.accent}22 0%, transparent 70%), linear-gradient(180deg, #14141f 0%, #0a0a15 100%)`,
        border: `1.5px solid ${medal.color}88`,
        boxShadow: `0 0 18px ${medal.color}55, inset 0 0 12px ${medal.color}22`,
      }}
      title={`${medal.name} — Rank ${medal.rank}`}
    >
      {/* Soft accent glow behind the artwork */}
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 50%, ${medal.accent}22 0%, transparent 65%)`,
          animation: animated ? 'medal-pulse 3.2s ease-in-out infinite' : 'none',
        }}
      />
      {/* Medal artwork — fills the tile at natural aspect */}
      <img
        src={medal.src}
        alt={medal.name}
        loading="lazy"
        draggable={false}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: `drop-shadow(0 0 6px ${medal.accent}aa)`,
          animation: animated ? 'medal-float 4.5s ease-in-out infinite' : 'none',
        }}
      />
      <style>{`
        @keyframes medal-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes medal-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-1.5px); }
        }
      `}</style>
    </div>
  );
}
